import { Brand } from '../config/brand.enum';
import { CourseTypes } from './iterable-params';

// Every Iterable list XCC checkout subscribes shoppers to. xcc-client picks the list; xcc-api accepts only these on
// `/iterable/subscribe`, and checkout ignores its 400.
// DEPLOY: xcc-client and xcc-api deploy separately, and a refused subscribe fails without a visible error. Adding a
// list: deploy xcc-api first, so it accepts the list before checkout sends it. Removing a list: deploy xcc-client
// first, so checkout stops sending it before the API refuses it.

/** Abandoned-cart lists, joined at email entry. Keyed by brand, then course type. */
export const ITERABLE_ABANDONED_CART_LISTS: Readonly<Partial<Record<Brand, Partial<Record<CourseTypes, number>>>>> = {
  [Brand.DEC]: {
    ADE: 764184,
    BTW: 764188,
    DD: 764183,
    DE: 764184,
    DIP: 764183,
    PTDE: 764184,
  },
  [Brand.IDS]: {
    ADE: 648623,
    DD: 648630,
    DE: 648623,
    DIP: 648630,
    PTDE: 648623,
  },
  [Brand.AA]: {
    [CourseTypes.AA]: 173559,
  },
  [Brand.ACE]: {
    ADE: 147056,
    AMI: 147056,
    DD: 152132,
    DE: 147056,
    PTDE: 147056,
  },
  [Brand.PA]: {
    [CourseTypes.PA]: 4233273,
  },
  [Brand.INS]: {
    [CourseTypes.INS]: 5180297,
  },
  [Brand.MTG]: {
    [CourseTypes.MTG]: 5180301,
  },
  // Dedicated "Abandoned Cart" list.
  [Brand.HSP]: {
    [CourseTypes.HSP]: 10981042,
  },
};

/** ACE's DE abandoned-cart list for a parent buyer, in place of `ITERABLE_ABANDONED_CART_LISTS.ACE.DE`. */
export const ACE_DE_PARENT_ABANDONED_CART_LIST = 147055;

const AA_PROMO_LISTS: Readonly<Record<string, Readonly<{ promotional: number; transactional: number }>>> =
  Object.freeze({ default: Object.freeze({ promotional: 10741950, transactional: 5164829 }) });

/**
 * Promotional (promo checkbox) and transactional (post-purchase) lists. Keyed by brand, then course type or `default`.
 */
export const ITERABLE_PROMO_LISTS: Readonly<
  Partial<Record<Brand, Readonly<Record<string, Readonly<{ promotional: number; transactional: number }>>>>>
> = {
  [Brand.ACE]: {
    [CourseTypes.DD]: { promotional: 7380153, transactional: 7380178 },
    default: { promotional: 5164789, transactional: 5164791 },
  },
  [Brand.AA]: AA_PROMO_LISTS,
  // The PA build picks these under PA but posts them as AA, so they are AA's.
  [Brand.PA]: AA_PROMO_LISTS,
  [Brand.IDS]: {
    default: { promotional: 5164869, transactional: 5164870 },
  },
  [Brand.DEC]: {
    default: { promotional: 5164851, transactional: 7787597 },
  },
  [Brand.INS]: {
    default: { promotional: 9012837, transactional: 9012824 },
  },
  [Brand.MTG]: {
    default: { promotional: 9012839, transactional: 9012832 },
  },
  // 11042998 = "Marketing Opt-in checkbox at checkout" (promo checkbox);
  // 11043000 = "Transactional SMS" - the transactional slot fires post-purchase
  // (subscribeUserToList) with the checkout phone number.
  [Brand.HSP]: {
    default: { promotional: 11042998, transactional: 11043000 },
  },
};

/**
 * "Payment failed" lists, so Nurture can message a buyer whose payment failed. Keyed by Iterable brand, so PrepAgent
 * buyers join AA's list, as they do for abandoned cart.
 */
export const ITERABLE_PAYMENT_FAILED_LISTS: Readonly<Partial<Record<Brand, number>>> = {
  [Brand.AA]: 11327264,
  [Brand.INS]: 11327254,
  [Brand.MTG]: 11327253,
};

/** Every list a brand's checkout may subscribe to. Empty for a brand with none (AARP) or an unknown brand. */
export const getIterableSubscribeListIds = (brand: string): ReadonlySet<number> => {
  // Own brand keys only, so `__proto__` or `constructor` can't read the prototype.
  if (!(Object.values(Brand) as string[]).includes(brand)) return new Set();
  const key = brand as Brand;
  const ids = new Set<number>(Object.values(ITERABLE_ABANDONED_CART_LISTS[key] ?? {}));
  if (key === Brand.ACE) ids.add(ACE_DE_PARENT_ABANDONED_CART_LIST);
  Object.values(ITERABLE_PROMO_LISTS[key] ?? {}).forEach(({ promotional, transactional }) => {
    ids.add(promotional);
    ids.add(transactional);
  });
  const paymentFailed = ITERABLE_PAYMENT_FAILED_LISTS[key];
  if (paymentFailed) ids.add(paymentFailed);
  return ids;
};
